import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import UserService from 'services/user.service';
import { setActiveManagerOrg } from 'store/actions/User';
import { settingActiveOrgRequest } from 'store/sagas/Org';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import {
    APP_CONTEXT,
    canWorkInOrg,
    getAppContext,
    getSwitchTarget
} from 'utils/app-context';
import { markOrgSelectorSwitch } from 'utils/org-switch-intent';

// Opening what a notification action points at (see `getNotificationAction`). The page is the
// operator app's and only the org the notification belongs to shows it, so the user is taken to
// that org first:
// - already in it, in the operator app: just open the page;
// - in the operator app, another org: switch like the org selector does and let the user guard
//   open the page once the switch has landed (a page opened sooner is checked against the old
//   org and answers 404);
// - in the resident app (or an org switch that would leave the operator app): set the active
//   org and property of the operator side, then reload straight onto the page, because the
//   listeners and routes of the other app only start on a load, as in `useSwitchApp`.
export const useNotificationAction = ({ onDone } = {}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userData = useSelector(state => state.user.userData);
    const activeOrgId = useSelector(state => state.user.activeOrg?.org_id);
    const activeCalls = useSelector(state => state.calls?.active);
    const activeComs = useSelector(state => state.coms?.active);
    const [switching, setSwitching] = useState(false);

    // On a call the user stays in the org the call belongs to.
    const onCall =
        Boolean(activeCalls?.some(call => call.fielded_by === userData?.uid)) ||
        Boolean(activeComs?.some(call => call.connected_by === userData?.uid));

    const openAction = useCallback(
        async action => {
            if (!action?.to || switching) return;

            const { orgId, orgName, to } = action;

            if (!orgId) {
                navigate(to);
                onDone?.();
                return;
            }

            const inOperatorApp = getAppContext(userData) === APP_CONTEXT.OPERATOR;

            if (
                inOperatorApp &&
                userData?.active_org_id === orgId &&
                activeOrgId === orgId
            ) {
                navigate(to);
                onDone?.();
                return;
            }

            if (onCall) {
                SnackbarNotify.error('Finish the call before switching organizations.');
                return;
            }

            if (!canWorkInOrg(userData, orgId)) {
                SnackbarNotify.error(
                    `You no longer have access to ${orgName || 'this organization'}.`
                );
                return;
            }

            // Setting the org alone keeps them in the operator app: the same switch as the org
            // selector's, which also stops the listeners of the org they leave. The root waits
            // there for it to land, then opens `to`.
            if (
                inOperatorApp &&
                getAppContext({ ...userData, active_org_id: orgId }) ===
                    APP_CONTEXT.OPERATOR
            ) {
                markOrgSelectorSwitch(orgId, to);
                navigate('/');
                dispatch(
                    setActiveManagerOrg({
                        org_id: orgId,
                        org_name: orgName || '',
                        origin: 'notification'
                    })
                );
                onDone?.();
                return;
            }

            const target = getSwitchTarget(userData, APP_CONTEXT.OPERATOR, { orgId });

            if (!target) {
                SnackbarNotify.error(
                    `Could not open the Operator app for ${
                        orgName || 'this organization'
                    }. Please switch to it from the profile menu.`
                );
                return;
            }

            setSwitching(true);

            try {
                if (target.propertyId) {
                    // Also sets the active org to the property's org.
                    await UserService.setActiveProperty(userData.uid, target.propertyId);
                } else {
                    await settingActiveOrgRequest({
                        active_org_id: target.orgId,
                        active_station_id: userData.active_station_id,
                        userId: userData.uid
                    });
                }

                window.location.assign(to);
            } catch (error) {
                console.error('Error opening the notification:', error);
                SnackbarNotify.error('Could not switch the app. Please try again.');
                setSwitching(false);
            }
        },
        [activeOrgId, dispatch, navigate, onCall, onDone, switching, userData]
    );

    return { openAction, switching };
};
